// src/services/expenseService.js
import api from '../utils/api';

const expenseService = {
  // Get all expenses with optional filtering
  getExpenses: async (filters = {}) => {
    try {
      const response = await api.get('/expenses', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching expenses:', error);
      return { data: [], pagination: { total: 0, pages: 0 } };
    }
  },

  // Get expenses for a specific branch
  getBranchExpenses: async (branchId, timeRange = '30d', filters = {}) => {
    try {
      const params = { branchId, timeRange, ...filters };
      const response = await api.get('/expenses', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching branch expenses:', error);
      return { data: [], pagination: { total: 0, pages: 0 } };
    }
  },

  // Get daily expenses for analytics. options: string (timeRange e.g. '180d') or { timeRange?, date?, fromDate?, toDate? }
  getDailyExpenses: async (branchId, options = '180d') => {
    try {
      const params = { branchId };
      if (typeof options === 'string') {
        params.timeRange = options;
      } else if (options?.fromDate && options?.toDate) {
        params.fromDate = options.fromDate;
        params.toDate = options.toDate;
      } else if (options?.date) {
        params.date = options.date;
      } else {
        params.timeRange = options?.timeRange || '180d';
      }
      const response = await api.get('/expenses/daily', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching daily expenses:', error);
      return [];
    }
  },

  // Get expenses by date range for analytics
  getExpensesByDateRange: async (branchId, startDate, endDate) => {
    try {
      const params = { branchId, startDate, endDate };
      const response = await api.get('/expenses', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching expenses by date range:', error);
      return { data: [], pagination: { total: 0, pages: 0 } };
    }
  },

  // Create new expense
  createExpense: async (expenseData) => {
    try {
      // 8s: on a dead network this must fail fast so the thunk drops the
      // expense into the offline queue instead of hanging the form ~30s.
      const response = await api.post('/expenses', expenseData, { timeout: 8000 });
      return response.data;
    } catch (error) {
      console.error('Error creating expense:', error);
      throw error;
    }
  },

  // Update expense
  updateExpense: async (expenseId, updateData) => {
    try {
      const response = await api.put(`/expenses/${expenseId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating expense:', error);
      throw error;
    }
  },

  // Delete expense
  deleteExpense: async (expenseId) => {
    try {
      const response = await api.delete(`/expenses/${expenseId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting expense:', error);
      throw error;
    }
  },

  // Get expense categories
  getExpenseCategories: async () => {
    try {
      const response = await api.get('/expenses/categories');
      return response.data;
    } catch (error) {
      console.error('Error fetching expense categories:', error);
      return [];
    }
  },

  // Get expense statistics for analytics
  getExpenseStats: async (branchId, timeRange = '30d') => {
    try {
      const params = { branchId, timeRange };
      const response = await api.get('/expenses/stats', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching expense stats:', error);
      return {
        totalExpenses: 0,
        averageDailyExpenses: 0,
        topExpenseCategories: [],
        expensesByDay: []
      };
    }
  },

  // Process OCR text with OpenAI. Pass { isHandwritten } so the parser treats
  // handwritten notes as a list of individual expense lines.
  processOCRText: async (ocrText, opts = {}) => {
    try {
      const response = await api.post('/expenses/process-ocr-text', {
        ocrText,
        isHandwritten: opts.isHandwritten === true
      });
      return response.data;
    } catch (error) {
      console.error('Error processing OCR text:', error);
      throw error;
    }
  },

  // Bulk-create expenses (used for handwritten notes → one expense per line)
  bulkCreateExpenses: async (expenses) => {
    try {
      const response = await api.post('/expenses/bulk', { expenses });
      return response.data;
    } catch (error) {
      console.error('Error bulk creating expenses:', error);
      throw error;
    }
  },

  // Get expense report with item-level breakdown
  getExpenseReport: async (filters = {}) => {
    try {
      const response = await api.get('/expenses/report', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching expense report:', error);
      throw error;
    }
  },

  // Personal fund withdrawals (e.g. "Huzur Hakkı" bank draws) summed per person for
  // a branch/date range - never counted as expenses, used for the Excel report footer
  getPersonalFundSummary: async (branchId, startDate, endDate) => {
    try {
      const params = { branchId };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await api.get('/expenses/personal-fund-summary', { params });
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching personal fund summary:', error);
      return [];
    }
  },

  // Raw personal fund withdrawal entries (date + name + amount + note), for the
  // "Personal" Excel export button and the personal-withdrawal entry form
  getPersonalFundWithdrawals: async (branchId, startDate, endDate) => {
    try {
      const params = { branchId };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await api.get('/expenses/personal-fund-withdrawals', { params });
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching personal fund withdrawals:', error);
      return [];
    }
  },

  createPersonalFundWithdrawal: async (payload) => {
    const response = await api.post('/expenses/personal-fund-withdrawals', payload);
    return response.data;
  },

  deletePersonalFundWithdrawal: async (id) => {
    const response = await api.delete(`/expenses/personal-fund-withdrawals/${id}`);
    return response.data;
  },

  getMerchantNames: async (params = {}) => {
    try {
      const response = await api.get('/expenses/merchant-names', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching merchant names:', error);
      throw error;
    }
  },

  // Quick suggestions — autocomplete (pass q) or frequent quick-add chips (no q)
  getQuickSuggestions: async (params = {}) => {
    try {
      const response = await api.get('/expenses/quick-suggestions', { params });
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching quick suggestions:', error);
      return [];
    }
  },

  // ── Recurring expenses ───────────────────────────────────────────────────────
  getRecurringExpenses: async (params = {}) => {
    try {
      const response = await api.get('/expenses/recurring/list', { params });
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching recurring expenses:', error);
      return [];
    }
  },
  createRecurringExpense: async (payload) => {
    const response = await api.post('/expenses/recurring', payload);
    return response.data;
  },
  updateRecurringExpense: async (id, payload) => {
    const response = await api.put(`/expenses/recurring/${id}`, payload);
    return response.data;
  },
  deleteRecurringExpense: async (id) => {
    const response = await api.delete(`/expenses/recurring/${id}`);
    return response.data;
  },

  // ── Recurring drafts awaiting confirmation ───────────────────────────────────
  getPendingDrafts: async (params = {}) => {
    try {
      const response = await api.get('/expenses/drafts/list', { params });
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching pending drafts:', error);
      return [];
    }
  },
  confirmDraft: async (id) => {
    const response = await api.post(`/expenses/drafts/${id}/confirm`);
    return response.data;
  },
  dismissDraft: async (id) => {
    const response = await api.delete(`/expenses/drafts/${id}`);
    return response.data;
  }
};

export default expenseService;
